import type { QueryClient, QueryKey } from "@tanstack/react-query";

import type { CopilotResourceRef } from "src/clients/agent-management";
import type { WorkspaceDataplane } from "src/clients/flow-api";
import {
  CONTEXTUAL_ENTITY_DEFINITIONS,
  isReferenceContextualEntityType,
} from "src/unifiedCopilot/context/entityDefinitions";
import type { ReferenceContextualEntity } from "src/unifiedCopilot/context/types";
import { mapContextualEntityRefToResourceRef } from "src/unifiedCopilot/context/utils";

export type EntityResolutionContext = {
  orgId: string;
  wsId: string;
  /** The current workspace, for resolvers that need it. */
  workspace: WorkspaceDataplane;
  /** The merged mention catalogue (ambient + global) — already in memory. */
  entities: ReferenceContextualEntity[];
  /**
   * Fetch transport. Resolvers go through ensureQueryData/fetchQuery so
   * react-query owns caching and staleness — a warm cache resolves without a
   * request.
   */
  queryClient: QueryClient;
};

/**
 * A ref its own resolver completed, carrying what the resource is called when
 * completing it meant reading a record that says so. The resolver holds that
 * record already typed, so it selects the name field itself and nothing
 * downstream needs to know which field that was.
 */
export type ResolvedContextualEntityRef = CopilotResourceRef & {
  displayName?: string;
};

/**
 * Completes a ref of one entity type from its id — parent included when the
 * type has one, display name when a record it read carries one.
 */
export type ContextualEntityResolver = (
  id: string,
  context: EntityResolutionContext,
) => Promise<ResolvedContextualEntityRef | null>;

/** One ref's resolution as a query, for a caller that observes it. */
export type ContextualEntityResolutionQuery = {
  queryKey: QueryKey;
  queryFn: () => Promise<ResolvedContextualEntityRef | null>;
  retry: false;
  throwOnError: false;
};

/**
 * A type's resolver as a query, so that everything observing one resource
 * shares a single resolution. Undefined for a type that resolves nothing.
 * Never retried: a resolution fails because the resource is gone or
 * inaccessible, and a second attempt answers the same. Never escalated to the
 * error boundary either: an unresolvable ref is a pill that stays inert under
 * the label it already carries.
 */
export const entityResolutionQueryOptions = (
  ref: CopilotResourceRef,
  context: EntityResolutionContext,
): ContextualEntityResolutionQuery | undefined => {
  const resolve = isReferenceContextualEntityType(ref.type)
    ? CONTEXTUAL_ENTITY_DEFINITIONS[ref.type].resolve
    : undefined;
  return resolve
    ? {
        // Scoped to the workspace: the names a resolution carries come off
        // the workspace host's records, and the cache outlives a switch
        // between them.
        queryKey: [
          "contextualEntityResolution",
          context.wsId,
          ref.type,
          ref.id,
        ],
        queryFn: () => resolve(ref.id, context),
        retry: false,
        throwOnError: false,
      }
    : undefined;
};

const parentFromCatalogue = (
  ref: CopilotResourceRef,
  context: EntityResolutionContext,
): CopilotResourceRef | undefined => {
  const entity = context.entities.find(
    (candidate) => candidate.type === ref.type && candidate.id === ref.id,
  );
  return entity?.parent
    ? mapContextualEntityRefToResourceRef(entity.parent)
    : undefined;
};

const resolveParent = async (
  ref: CopilotResourceRef,
  context: EntityResolutionContext,
): Promise<CopilotResourceRef | undefined> => {
  if (ref.parent) {
    return ref.parent;
  }
  const catalogueParent = parentFromCatalogue(ref, context);
  if (catalogueParent) {
    return catalogueParent;
  }
  const resolve = isReferenceContextualEntityType(ref.type)
    ? CONTEXTUAL_ENTITY_DEFINITIONS[ref.type].resolve
    : undefined;
  return (await resolve?.(ref.id, context))?.parent ?? undefined;
};

/**
 * Completes a ref's ancestor chain by traversing upward. Each level's parent
 * comes from the ref's own chain first, then the entity catalogue (already in
 * memory), then the type's resolver (react-query-cached fetch); the recursion
 * ends at a level with no parent left to discover (chain root or dead end).
 */
export const resolveEntityRefChain = async (
  ref: CopilotResourceRef,
  context: EntityResolutionContext,
): Promise<CopilotResourceRef> => {
  const parent = await resolveParent(ref, context);
  if (!parent) {
    return { type: ref.type, id: ref.id };
  }
  return {
    type: ref.type,
    id: ref.id,
    parent: await resolveEntityRefChain(parent, context),
  };
};
